import get from 'immutable-less/transmute/get';
import memoize from 'transmute/memoize';
import { List, OrderedMap } from 'immutable';
import ConnectionTypes from 'automation-ui-action-features/constants/ConnectionTypes';
import ConnectionKeys from 'automation-ui-action-features/constants/ConnectionKeys';
import * as BranchConnectorKeys from 'automation-ui-action-features/constants/BranchConnectorKeys';
import * as ConnectorEdgeTypes from 'automation-ui-action-features/constants/ConnectorEdgeTypes';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import SingleConnection from 'automation-ui-action-features/records/connection/SingleConnection';
import ListBranchConnection from 'automation-ui-action-features/records/connection/ListBranchConnection';
import StaticBranchConnection from 'automation-ui-action-features/records/connection/StaticBranchConnection';
import LoopConnection from 'automation-ui-action-features/records/connection/LoopConnection';
import BranchConnector from 'automation-ui-action-features/records/connection/BranchConnector';
import Action from 'automation-ui-action-features/records/Action';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import { getActionMetadataConfigFromAction } from 'automation-ui-action-features/actions/ActionMetadataConfigUtils';
import ABTestBranch from 'automation-ui-action-features/records/ABTestBranch';
import ABTestBranchConnection from 'automation-ui-action-features/records/connection/ABTestBranchConnection';
export const getId = get('actionId');
export const getActionType = get('actionType');
export const getMetadata = get('metadata');
export const getConnection = get('connection');
export const getListBranches = get(ConnectionKeys.LIST_BRANCHES);
export const getStaticBranches = get(ConnectionKeys.STATIC_BRANCHES);
export const getLoopBranch = get(ConnectionKeys.LOOP_BRANCH);

// Updates also need to be reflected in BranchConnectionMixin.
export const DEFAULT_BRANCH_INDEX = -1;

// Updates also need to be reflected in BranchConnectionMixin.
export function isDefaultBranchIndex(branchIndex) {
  return branchIndex === DEFAULT_BRANCH_INDEX;
}
export function isGoToActionId(actionId) {
  return String(actionId).includes(SpecialActionIds.GO_TO);
}

/**
 * A goToActionId is a string of the form:
 *   '${previousActionEdgeId}-goToAction-${goToActionidCounter}'
 */
export function getEdgeIdFromGoToActionId(goToActionId) {
  return goToActionId.split(`-${SpecialActionIds.GO_TO}-`)[0];
}

/**
 * An edge is a string of the form:
 *   '${previousActionId}-edge-${edgeModifier}'
 */
export function getEdgeModifier(edgeId) {
  return Number(edgeId.split('-edge-')[1]);
}

/**
 * An edge is a string of the form:
 *   '${previousActionId}-edge-${edgeModifier}'
 * Returns DEFAULT_BRANCH_INDEX for the "NO" branch
 */
export function getBranchIndex(edgeId) {
  // Updates also need to be reflected in BranchConnectionMixin.
  return getEdgeModifier(edgeId) - 2;
}

/**
 * An edge is a string of the form:
 *   '${previousActionId}-edge-${edgeModifier}'
 *
 * Note: previousActionId can be 'new-action' instead of a number
 */
export function getPreviousActionId(edgeId) {
  const rawActionId = edgeId.split('-edge-')[0];
  return Number(rawActionId) || rawActionId;
}
export function isDefaultBranchEdge(edgeId) {
  return isDefaultBranchIndex(getBranchIndex(edgeId));
}
export function getConnectionTypeForActionType(actionType) {
  switch (actionType) {
    case ActionTypes.LIST_BRANCH:
      return ConnectionTypes.LIST_BRANCH_CONNECTION;
    case ActionTypes.STATIC_BRANCH:
      return ConnectionTypes.STATIC_BRANCH_CONNECTION;
    case ActionTypes.AB_TEST_BRANCH:
      return ConnectionTypes.AB_TEST_BRANCH_CONNECTION;
    case ActionTypes.WHILE_LOOP:
      return ConnectionTypes.WHILE_LOOP;
    default:
      return null;
  }
}
export function createSingleConnection(nextActionId, nextActionEdgeType = ConnectorEdgeTypes.STANDARD) {
  if (!nextActionId) {
    return null;
  }
  return new SingleConnection({
    [ConnectionKeys.NEXT_ACTION_ID]: nextActionId,
    [ConnectionKeys.CONNECTION_TYPE]: ConnectionTypes.SINGLE_CONNECTION,
    [ConnectionKeys.EDGE_TYPE]: nextActionEdgeType
  });
}
export function createListBranchConnection({
  listBranches,
  nextActionId = null,
  nextActionEdgeType = ConnectorEdgeTypes.STANDARD,
  branchIndexToSetDownstreamActionsIn,
  actionType = ActionTypes.LIST_BRANCH,
  defaultBranchName
}) {
  const connector = nextActionId ? new BranchConnector({
    [BranchConnectorKeys.NEXT_ACTION_ID]: nextActionId,
    [BranchConnectorKeys.EDGE_TYPE]: nextActionEdgeType || ConnectorEdgeTypes.STANDARD
  }) : null;
  let connection = new ListBranchConnection({
    [ConnectionKeys.CONNECTION_TYPE]: getConnectionTypeForActionType(actionType),
    [ConnectionKeys.LIST_BRANCHES]: List(listBranches).toJS(),
    [ConnectionKeys.DEFAULT_BRANCH_NAME]: defaultBranchName
  });
  connection = connection.setConnectorByBranchIndex(branchIndexToSetDownstreamActionsIn || 0, connector);
  return connection;
}
export function createStaticBranchConnection({
  branches,
  defaultBranchName,
  nextActionId = null,
  nextActionEdgeType = ConnectorEdgeTypes.STANDARD,
  branchIndexToSetDownstreamActionsIn,
  actionType = ActionTypes.STATIC_BRANCH
}) {
  const connector = nextActionId ? new BranchConnector({
    [BranchConnectorKeys.NEXT_ACTION_ID]: nextActionId,
    [BranchConnectorKeys.EDGE_TYPE]: nextActionEdgeType || ConnectorEdgeTypes.STANDARD
  }) : null;
  let connection = new StaticBranchConnection({
    [ConnectionKeys.CONNECTION_TYPE]: getConnectionTypeForActionType(actionType),
    [ConnectionKeys.STATIC_BRANCHES]: List(branches).toJS(),
    [ConnectionKeys.DEFAULT_BRANCH_NAME]: defaultBranchName
  });
  connection = connection.setConnectorByBranchIndex(branchIndexToSetDownstreamActionsIn || 0, connector);
  return connection;
}

// Loop actions are always created with a default `connector` that
// contains a GOTO connection back to the starting loop action
// therefore there is no need to modify the existing `connector`
export function createLoopBranchConnection({
  branches,
  defaultBranchName,
  actionType = ActionTypes.WHILE_LOOP
}) {
  const connection = new LoopConnection({
    [ConnectionKeys.CONNECTION_TYPE]: getConnectionTypeForActionType(actionType),
    [ConnectionKeys.LOOP_BRANCH]: branches.get(0),
    [ConnectionKeys.DEFAULT_BRANCH_NAME]: defaultBranchName
  });
  return connection;
}

// ABTest branches have no default connections
export function createABTestBranchConnection({
  branches,
  actionType = ActionTypes.AB_TEST_BRANCH,
  nextActionId,
  nextActionEdgeType,
  branchIndexToSetDownstreamActionsIn
}) {
  const connector = nextActionId ? new BranchConnector({
    [BranchConnectorKeys.NEXT_ACTION_ID]: nextActionId,
    [BranchConnectorKeys.EDGE_TYPE]: nextActionEdgeType || ConnectorEdgeTypes.STANDARD
  }) : null;
  const defaultBranches = branches ? branches.toArray() : [new ABTestBranch(), new ABTestBranch()];
  let connection = new ABTestBranchConnection({
    connectionType: getConnectionTypeForActionType(actionType),
    connections: defaultBranches
  });
  connection = connection.setConnectorByBranchIndex(branchIndexToSetDownstreamActionsIn || 0, connector);
  return connection;
}

/**
 * Creates the connection of an action being added as the
 * first action in the flow.
 */
export function createConnectionForStartingAction({
  startingActionId,
  isCurrentActionListBranch,
  isCurrentActionStaticBranch,
  isCurrentActionABTestBranch,
  isCurrentActionLoop,
  branches,
  branchIndexToSetDownstreamActionsIn,
  actionType,
  defaultBranchName
}) {
  if (isCurrentActionListBranch) {
    return createListBranchConnection({
      listBranches: branches,
      defaultBranchName,
      nextActionId: startingActionId,
      nextActionEdgeType: ConnectorEdgeTypes.STANDARD,
      branchIndexToSetDownstreamActionsIn,
      actionType
    });
  }
  if (isCurrentActionStaticBranch) {
    return createStaticBranchConnection({
      branches: branches,
      defaultBranchName,
      nextActionId: startingActionId,
      nextActionEdgeType: ConnectorEdgeTypes.STANDARD,
      branchIndexToSetDownstreamActionsIn,
      actionType
    });
  }
  if (isCurrentActionLoop) {
    return createLoopBranchConnection({
      branches: branches,
      defaultBranchName,
      actionType
    });
  }
  if (isCurrentActionABTestBranch) {
    return createABTestBranchConnection({
      branches: branches,
      actionType,
      nextActionId: startingActionId,
      nextActionEdgeType: ConnectorEdgeTypes.STANDARD,
      branchIndexToSetDownstreamActionsIn
    });
  }
  return createSingleConnection(startingActionId, ConnectorEdgeTypes.STANDARD);
}
export function getNewConnection({
  action,
  previousAction,
  startingActionId,
  branches,
  edgeId,
  branchIndexToSetDownstreamActionsIn,
  defaultBranchName
}) {
  const isCurrentActionListBranch = action.isListBranch();
  const isCurrentActionStaticBranch = action.isStaticBranch();
  const isCurrentActionLoop = action.isLoopBranch();
  const isCurrentActionABTestBranch = action.isABTestBranch();
  if (!previousAction) {
    return createConnectionForStartingAction({
      startingActionId,
      isCurrentActionListBranch,
      isCurrentActionStaticBranch,
      isCurrentActionLoop,
      isCurrentActionABTestBranch,
      branches,
      defaultBranchName,
      branchIndexToSetDownstreamActionsIn,
      actionType: action.getActionType()
    });
  }
  const previousActionConnection = previousAction.getConnection();
  const isCurrentActionBranch = action.isBranch();
  const isPreviousActionBranch = previousAction.isBranch();

  // Adding a simple action after another simple action
  if (!isPreviousActionBranch && !isCurrentActionBranch) {
    return previousActionConnection;
  }
  const connector = previousActionConnection && previousActionConnection.getConnectorByEdgeId(edgeId);
  const nextActionId = connector && connector.getNextActionId();
  const nextActionEdgeType = connector && connector.getEdgeType();
  if (isCurrentActionListBranch) {
    return createListBranchConnection({
      listBranches: branches,
      defaultBranchName,
      nextActionId,
      nextActionEdgeType: nextActionEdgeType,
      branchIndexToSetDownstreamActionsIn,
      actionType: action.getActionType()
    });
  }
  if (isCurrentActionStaticBranch) {
    return createStaticBranchConnection({
      branches: branches,
      defaultBranchName,
      nextActionId,
      nextActionEdgeType: nextActionEdgeType,
      branchIndexToSetDownstreamActionsIn,
      actionType: action.getActionType()
    });
  }
  if (isCurrentActionLoop) {
    return createLoopBranchConnection({
      branches: branches,
      defaultBranchName,
      actionType: action.getActionType()
    });
  }
  if (isCurrentActionABTestBranch) {
    return createABTestBranchConnection({
      branches: branches,
      actionType: action.getActionType(),
      nextActionId,
      nextActionEdgeType: nextActionEdgeType,
      branchIndexToSetDownstreamActionsIn
    });
  }
  if (!nextActionId) {
    return null;
  }
  if (isPreviousActionBranch && !isCurrentActionBranch) {
    return createSingleConnection(nextActionId, nextActionEdgeType);
  }
  return previousActionConnection;
}
export const createActionMap = flow => {
  return OrderedMap(Object.keys(flow.actions).map(actionIdStr => [Number(actionIdStr), new Action(flow.actions[actionIdStr])]));
};
/**
 * Creates a reverse edge map from a Flow's actions.
 */
export const createReverseEdgeMap = memoize(actions => {
  const reverseEdges = {};
  if (actions) {
    actions.forEach(action => {
      const nodeActionId = action.getActionId();
      const connection = action.getConnection();
      if (!connection) {
        return;
      }
      connection.getConnectors().forEach((connector, index) => {
        if (!connector) {
          return;
        }
        const nextActionId = connector.getNextActionId();
        const cachedEdgeConnections = reverseEdges[nextActionId] || [];
        const edgeType = connector.getEdgeType();
        const nextAction = actions.get(nextActionId);
        const isLoopReturnEdge = nextAction && nextAction.isLoopBranch() && edgeType === ConnectorEdgeTypes.GOTO;

        // if an edge is a "loop return edge", don't traverse
        // because that node will already be accounted for
        if (!isLoopReturnEdge) {
          cachedEdgeConnections.push({
            edgeId: `${nodeActionId}-edge-${index + 1}`,
            edgeType
          });
          reverseEdges[nextActionId] = cachedEdgeConnections;
        }
      });
    });
  }
  return reverseEdges;
}, actions => `${actions && uniqueIdForObject(actions)}`);

/**
 *  Given an actionId, find the "master" parent edge by finding
 *  the one edge NOT in the childToParentEdges map for that
 *  action
 */
export function getMasterPreviousEdge(actionId, childToParentEdges) {
  const previousParentEdges = childToParentEdges[actionId];
  if (!previousParentEdges) {
    return null;
  }
  return previousParentEdges.filter(parentEdge => parentEdge.edgeType === ConnectorEdgeTypes.STANDARD)[0];
}
/**
 * Runs a BFS on the actions (starting at the first action id)
 * and creates a map from actionId to a 1-indexed counter. It
 * must be supplied an `actionIdMap` WITHOUT go to edges
 * otherwise this doesn't work correctly.
 */
export const actionCounterBfsTraversal = ({
  firstActionId,
  actionIdMap
}) => {
  let latestLabelCounter = 1;
  const accumulatedLabels = {};
  const visitedActionIds = new Set();
  const actionIdQueue = [firstActionId];
  while (actionIdQueue.length) {
    const currentActionId = actionIdQueue.shift();
    accumulatedLabels[currentActionId] = latestLabelCounter++;
    const {
      children
    } = actionIdMap[currentActionId] || {};
    if (children) {
      for (const actionId of children) {
        if (!visitedActionIds.has(actionId)) {
          visitedActionIds.add(actionId);
          actionIdQueue.push(actionId);
        }
      }
    }
  }
  return accumulatedLabels;
};
const findAllAncestorsActionIds = memoize(({
  actionIdMap,
  actions,
  rootActionId,
  isNewAction
}) => {
  const rootActionIdNode = actionIdMap[rootActionId];
  if (!rootActionIdNode) {
    return [];
  }

  /*
    The isNewAction flag is a signal that the actionId passed to the fn is actually the action immediately preceeding a new action in the graph, which is an alternative to passing `new-action` as the actionId (which is not supported). This will ensure that the `actionId` is included as an ancestor to the new action in the output of this function
  */
  let nextNodeActionIds = isNewAction ? [rootActionId] : rootActionIdNode.parents;
  const visited = [...nextNodeActionIds];
  const nodeActionIdStack = [...nextNodeActionIds];
  const nodeActionIds = [];
  while (nodeActionIdStack.length > 0) {
    const currActionId = nodeActionIdStack.pop();
    const currActionIdNode = actionIdMap[currActionId];
    nextNodeActionIds = currActionIdNode.parents;
    if (actions.get(currActionId) !== undefined) {
      nodeActionIds.push(currActionId);
    }
    for (const nextActionId of nextNodeActionIds) {
      if (!visited.includes(nextActionId)) {
        nodeActionIdStack.push(nextActionId);
        visited.push(nextActionId);
      }
    }
  }
  return nodeActionIds;
}, ({
  actionIdMap,
  actions,
  rootActionId,
  isNewAction
}) => `${uniqueIdForObject(actionIdMap)}-${uniqueIdForObject(actions)}-${rootActionId}-${isNewAction}`);
export const findAllDecendantsActionIds = memoize(({
  actionIdMap,
  actions,
  rootActionId
}) => {
  const rootActionIdNode = actionIdMap[rootActionId];
  if (!rootActionIdNode) {
    return [];
  }
  let nextNodeActionIds = rootActionIdNode.children;
  const visited = [...nextNodeActionIds];
  const nodeActionIdStack = [...nextNodeActionIds];
  const nodeActionIds = [];
  while (nodeActionIdStack.length > 0) {
    const currActionId = nodeActionIdStack.pop();
    const currActionIdNode = actionIdMap[currActionId];
    nextNodeActionIds = currActionIdNode.children;
    if (actions.get(currActionId) !== undefined) {
      nodeActionIds.push(currActionId);
    }
    for (const nextActionId of nextNodeActionIds) {
      if (!visited.includes(nextActionId)) {
        nodeActionIdStack.push(nextActionId);
        visited.push(nextActionId);
      }
    }
  }
  return nodeActionIds;
}, ({
  actionIdMap,
  actions,
  rootActionId,
  isNewAction
}) => `${uniqueIdForObject(actionIdMap)}-${uniqueIdForObject(actions)}-${rootActionId}-${isNewAction}`);

/** use to more easily run graph algorithms
 *  actionIdMap = {
 *    actionId1: {
 *      parents: [parentActionId1, parentActionId2, ...],
 *      children: [childActionId1, childActionId2, ...]
 *    },
 *    .
 *    .
 *    .
 * }
 *
 */

export const createActionIdMap = memoize((actions, includeGoToEdges = true) => {
  const reverseEdgeMap = createReverseEdgeMap(actions);
  const actionIdMap = {};
  const ActionIdNode = function ActionIdNode() {
    // @ts-expect-error TS doesn't support constructor functions
    this.children = [];
    // @ts-expect-error TS doesn't support constructor functions
    this.parents = [];
  };
  if (Object.keys(reverseEdgeMap).length === 0) {
    actions.forEach(action => {
      // @ts-expect-error TS doesn't support constructor functions
      actionIdMap[action.getActionId()] = new ActionIdNode();
    });
  }
  Object.keys(reverseEdgeMap).forEach(childActionIdStr => {
    const childActionId = Number(childActionIdStr) || childActionIdStr;
    if (!Object.prototype.hasOwnProperty.call(actionIdMap, childActionId)) {
      // @ts-expect-error TS doesn't support constructor functions
      actionIdMap[childActionId] = new ActionIdNode();
    }
    reverseEdgeMap[childActionId].forEach(({
      edgeId,
      edgeType
    }) => {
      const parentActionId = getPreviousActionId(edgeId);
      if (!Object.prototype.hasOwnProperty.call(actionIdMap, parentActionId)) {
        // @ts-expect-error TS doesn't support constructor functions
        actionIdMap[parentActionId] = new ActionIdNode();
      }

      // @ts-expect-error TS doesn't support constructor functions
      actionIdMap[childActionId].parents.push(parentActionId);
      if (includeGoToEdges || edgeType !== ConnectorEdgeTypes.GOTO)
        // @ts-expect-error TS doesn't support constructor functions
        actionIdMap[parentActionId].children.push(childActionId);
    });
  });
  return actionIdMap;
}, (actions, includeGoToEdges) => `${uniqueIdForObject(actions)}-${includeGoToEdges}`);

/**
 * Given a flow, this method will traverse the graph of actions
 * and generate 1-indexed, incrementing counters for each
 * action in a top to bottom, left to right fashion (BFS).
 */
export const createActionCardCounters = memoize((actions, firstActionId) => {
  let latestLabelCounter = 1;
  const accumulatedLabels = {};
  const actionIdsToLabelQueue = [firstActionId];
  while (actionIdsToLabelQueue.length) {
    const currentActionId = actionIdsToLabelQueue.shift();
    const currentAction = actions.get(currentActionId);
    if (currentActionId !== SpecialActionIds.NEW_ACTION) {
      accumulatedLabels[currentActionId] = latestLabelCounter++;
    }
    if (!currentAction || currentAction.isLeafNode()) {
      continue;
    }
    let connectors = currentAction.getConnection().getConnectors();
    if (currentAction.getConnection().isBranchConnection()) {
      const defaultBranch = currentAction.getConnection()
      // @ts-expect-error default connector is only on some actions, but checked by the
      // isBranchConnection() check above
      .getDefaultConnector();
      connectors = connectors.filter(connector => connector !== defaultBranch);
      connectors.push(defaultBranch);
    }
    for (const connector of connectors) {
      if (connector !== null && !connector.isGoToEdge()) {
        actionIdsToLabelQueue.push(connector.getNextActionId());
      }
    }
  }
  return accumulatedLabels;
}, (actions, firstActionId) => `${uniqueIdForObject(actions)}-${firstActionId}`);

/**
 * Returns an object containing two arrays of action IDs, one representing all
 * ancestors of the given action and the other all descendants. If isNewAction
 * is true, the given action ID will be included in the list of ancestors.
 *
 * IMPORTANT NOTE: When isNewAction is true it is generally assumed that the
 * actionId belongs to the parent action.  If the parent action has multiple
 * branches this util has no way of knowing which branch the new action belongs
 * to and will include the descendants of all branches as if they were
 * descendants of the new action.  It is recommended to pass actionId as
 * "new-action" and pass isNewAction as false if the list of descendants must be
 * accurate to the new aciton.
 */
export const findAllAncestorsAndDecendantsOfAction = (actions, actionId, isNewAction) => {
  const actionIdMap = createActionIdMap(actions);
  const ancestors = findAllAncestorsActionIds({
    actionIdMap,
    actions,
    rootActionId: actionId,
    isNewAction
  }).map(ancestorActionId => actions.get(ancestorActionId));
  const descendants = findAllDecendantsActionIds({
    actionIdMap,
    actions,
    rootActionId: actionId
  }).map(descendantActionId => actions.get(descendantActionId));
  return {
    ancestors,
    descendants
  };
};

/**
 * Given the map of actionTypeId to output definitions,
 * this utility makes use of the flow's methods to find
 * all ancestors for a given targetActionId and proceeds
 * to generate an array of outputs available for those
 * ancestors.
 */
export const getActionOutputsForTargetActionById = memoize(({
  actions,
  allOutputsByActionTypeId,
  instanceOutputsByActionId,
  targetActionId,
  isNewAction
}) => {
  if (!allOutputsByActionTypeId && !instanceOutputsByActionId) {
    return [];
  }
  const ancestors = findAllAncestorsAndDecendantsOfAction(actions, targetActionId, isNewAction).ancestors;
  const actionOutputsForTargetAction = ancestors.map(ancestor => {
    const actionId = ancestor.getActionId();
    const actionTypeId = ancestor.getActionTypeId();
    if (!actionTypeId) {
      return null;
    }
    const instanceFieldDefintions = instanceOutputsByActionId && instanceOutputsByActionId[actionId];
    const actionFieldDefinitions = allOutputsByActionTypeId && allOutputsByActionTypeId[actionTypeId];
    const outputFieldDefinitions = [...(instanceFieldDefintions || []), ...(actionFieldDefinitions || [])];
    if (!outputFieldDefinitions.length) {
      return null;
    }
    return {
      actionId,
      outputFieldDefinitions
    };
  }).filter(output => !!output);

  // Null values have been filtered out
  return actionOutputsForTargetAction;
}, ({
  actions,
  allOutputsByActionTypeId,
  instanceOutputsByActionId,
  targetActionId,
  isNewAction
}) => `${uniqueIdForObject(actions)}-${allOutputsByActionTypeId && uniqueIdForObject(allOutputsByActionTypeId)}-${instanceOutputsByActionId && uniqueIdForObject(instanceOutputsByActionId)}-${targetActionId}-${isNewAction}`);

// This should ONLY be used to label actions when consolidated actions can't be supported
export function getUnconsolidatedActionTypeLabelKey(actionType) {
  switch (actionType) {
    case ActionTypes.DELAY_UNTIL_DATE:
      return 'automation-ui-action-features.action-features.delay-until-date.description';
    case ActionTypes.WAIT_UNTIL:
      return 'automation-ui-action-features.action-features.wait-until.description';
    case ActionTypes.DELAY:
      return 'automation-ui-action-features.action-features.all-delays.description';
    case ActionTypes.LIST_BRANCH:
      return 'automation-ui-action-features.action-features.list-branch.gatedDescription';
    case ActionTypes.STATIC_BRANCH:
      return 'automation-ui-action-features.action-features.static-branch.gatedDescription';
    case ActionTypes.AB_TEST_BRANCH:
      return 'automation-ui-action-features.action-features.ab-test-branch.gatedDescription';
    default:
      return undefined;
  }
}

/**
 * As an abstraction for canvas and action config rendering,
 * this function will generate a function at the Flow level
 * that may be used at a lower Action level to generate the
 * label of any arbitrary action.
 *
 * The returned function requires either the `actionId` or
 * the `action`. If an `action` is provided, it will override
 * the `actionId` with its own actionId.
 *
 * The `usePrefix` argument can be used to override the use
 * of the action label numbering.
 *
 * The `inferConfigSubType` argument can be used to override the
 * config sub type. This is important when using something like
 * data passing because we will always be looking at existing
 * metadata.
 */
export const getActionHeaderTitle = ({
  flowActions,
  firstActionId,
  configSubType,
  extensionDefinitions
}) => ({
  actionId: overrideActionId,
  action: overrideAction,
  useString,
  actionLink,
  usePrefix = true,
  inferConfigSubType = false
}) => {
  let actionId = Number(overrideActionId);
  if (overrideAction) {
    actionId = overrideAction.getActionId();
  }
  // TODO - @mlegocki: remove `flow &&` check since `flow` should always be defined here however we're running into a runtime error in some embed edge-cases. Issue to resolve before removing this: https://git.hubteam.com/HubSpot/AutomationTeam/issues/4836
  const action = overrideAction || flowActions.get(actionId);
  if (!action) {
    return null;
  }
  const actionDefinition = getActionMetadataConfigFromAction(action, inferConfigSubType ? undefined : configSubType, extensionDefinitions);
  let prefix;
  if (usePrefix) {
    const actionCardCounters = createActionCardCounters(flowActions, firstActionId);
    prefix = actionCardCounters[actionId];
  }
  return actionDefinition.getActionLabel(prefix, actionLink, useString, action);
};